<template>
    <div class="tw:w-full">
        <div v-if="localidades.length > 0 || forceMultiselect">
            <BaseMultiselect
                label="Localidad"
                name="localidades"
                :value="selectedId"
                :options="localidades"
                label-key="nombre"
                value-key="id"
                :disabled="disabled"
                rules="required"
                @input="onSelect"
            />
        </div>
        <div v-else>
            <input
                type="text"
                v-model="localidadTexto"
                :disabled="disabled"
                placeholder="Escribir localidad..."
                class="tw:block tw:p-2 tw:w-full tw:text-sm tw:rounded tw:border tw:border-slate-300 tw:bg-white tw:focus:outline-none tw:focus:border-blue-400"
                @input="onTextInput"
            />
        </div>
    </div>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import store from '@/store';
    import { config } from '@/config';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    const props = defineProps({
        modelValue: { type: [Number, Object, String], default: null },
        provincia: { type: [Number, Object], default: null },
        location_id: { type: Number, default: null },
        disabled: { type: Boolean, default: false },
        forceMultiselect: { type: Boolean, default: false }
    });

    const emit = defineEmits(['update:modelValue', 'localidadesCargadas']);

    const localidades = ref([]);
    const selectedId = ref(null);
    const localidadTexto = ref('');

    // Guard null-safe para un modelValue "objeto con id". La forma anterior
    // (`typeof val === 'object' && val !== null && val.id`) comparaba `val` con
    // null tras estrechar por typeof y usaba el `id` como booleano, así que un
    // id=0 se descartaba. `val && typeof val === 'object'` cubre null sin
    // compararlo explícitamente y `'id' in val` acepta el 0.
    function isObjectWithId(val) {
        return Boolean(val) && typeof val === 'object' && !Array.isArray(val) && 'id' in val;
    }

    function syncSelected(val) {
        if (!val || !localidades.value.length) {
            selectedId.value = null;
            return;
        }
        let found = null;
        if (typeof val === 'number') {
            found = localidades.value.find(l => l.id === val);
        } else if (isObjectWithId(val)) {
            found = localidades.value.find(l => l.id === val.id);
        } else if (typeof val === 'string') {
            found = localidades.value.find(l => l.nombre === val);
        }
        selectedId.value = found ? found.id : null;
    }

    function fetchLocations(prov) {
        if (!prov) {
            localidades.value = [];
            selectedId.value = null;
            syncSelected(props.modelValue);
            return;
        }

        const provinciaObj = typeof prov === 'object' ? prov : null;
        if (provinciaObj && provinciaObj.localidades) {
            localidades.value = provinciaObj.localidades;
            syncSelected(props.modelValue);
            emit('localidadesCargadas', localidades.value);
            return;
        }

        const provinciaId = typeof prov === 'number' ? prov : prov.id;
        store.dispatch(config.getVuexAlias('select.locations'), {
            filters: { provincia_id: provinciaId }
        }).then(res => {
            localidades.value = res.data.data || res.data || [];
            syncSelected(props.modelValue);
            emit('localidadesCargadas', localidades.value);
        }).catch(err => {
            console.error('Error fetching locations:', err);
            localidades.value = [];
        });
    }

    function onSelect(val) {
        const found = localidades.value.find(l => l.id === val);
        selectedId.value = val;
        emit('update:modelValue', found || val);
    }

    function onTextInput() {
        emit('update:modelValue', localidadTexto.value);
    }

    watch(() => props.provincia, newProv => {
        selectedId.value = null;
        localidadTexto.value = '';
        fetchLocations(newProv);
    });

    watch(() => props.modelValue, newVal => {
        syncSelected(newVal);
    });

    onMounted(() => {
        if (props.provincia) {
            fetchLocations(props.provincia);
        }
    });
</script>
